import { Check, X, Clock, Flame, Cloud, Server } from 'lucide-react'
import type { CellValue, ComparisonCategory } from './ClickStackAlternativePage.types'
import TrackingLink from '@/components/TrackingLink'
import FeatureComparisonGridWithOcclusion from '@/shared/components/molecules/FeaturePages/FeatureComparisonGridWithOcclusion'
import type { ComparisonSection } from '@/shared/components/molecules/FeaturePages/FeatureComparisonGrid'
import { buttonVariants } from '@/components/ui/Button'
import { cn } from 'app/lib/utils'

const BADGE_ICONS = {
  clock: Clock,
  flame: Flame,
  cloud: Cloud,
  server: Server,
} as const

function renderCell(value: CellValue) {
  switch (value.type) {
    case 'check':
      return (
        <div className="flex items-center gap-1.5">
          <Check size={15} className="shrink-0 text-green-400" />
          {value.label && <span className="text-sm text-[#adb4c2]">{value.label}</span>}
        </div>
      )
    case 'cross':
      return (
        <div className="flex items-center gap-1.5">
          <X size={15} className="shrink-0 text-red-400" />
          {value.label && <span className="text-sm text-[#adb4c2]">{value.label}</span>}
        </div>
      )
    case 'dash':
      return <span className="text-sm text-[#62687c]">&mdash;</span>
    case 'text':
      return <span className="text-sm leading-6 text-[#adb4c2]">{value.content}</span>
    case 'badge': {
      const Icon = BADGE_ICONS[value.icon]
      return (
        <div className="flex items-center gap-1.5">
          <Icon size={15} className="shrink-0 text-[#adb4c2]" />
          <span className="text-xs font-medium uppercase tracking-[0.48px] text-[#adb4c2]">
            {value.label}
          </span>
        </div>
      )
    }
  }
}

function cellToMarkdown(value: CellValue): string {
  switch (value.type) {
    case 'check':
      return value.label ? `✓ ${value.label}` : '✓'
    case 'cross':
      return value.label ? `✗ ${value.label}` : '✗'
    case 'dash':
      return '—'
    case 'text':
      return typeof value.content === 'string' ? value.content : ''
    case 'badge':
      return value.label
  }
}

function toSections(data: ComparisonCategory[]): ComparisonSection[] {
  return data.map((cat) => ({
    title: cat.category,
    rows: cat.rows.map((row) => ({
      feature: <span className="text-sm leading-6 text-[#adb4c2]">{row.feature}</span>,
      markdownFeature: row.feature,
      cells: {
        signoz: renderCell(row.signoz),
        clickstack: renderCell(row.clickstack),
      },
      markdownCells: {
        signoz: cellToMarkdown(row.signoz),
        clickstack: cellToMarkdown(row.clickstack),
      },
    })),
  }))
}

const GRID_CLASS = 'grid-cols-[1fr_12rem_12rem]'

const COLUMNS = [
  {
    key: 'signoz',
    cellClassName: 'relative px-3 py-3',
    sectionCellClassName: 'relative',
    occludeStickyText: true,
  },
  {
    key: 'clickstack',
    cellClassName: 'px-3 py-3',
    sectionCellClassName: 'bg-signoz_ink-500',
  },
]

export default function ComparisonGrid({ data }: { data: ComparisonCategory[] }) {
  const sections = toSections(data)

  return (
    <div className="w-full overflow-x-auto text-left text-base leading-normal md:overflow-visible">
      <div className="relative min-w-[40rem] md:min-w-0">
        <div className="pointer-events-none absolute inset-y-0 right-48 z-0 w-48 rounded-lg bg-gradient-to-b from-signoz_ink-300 from-[73%] to-transparent opacity-80" />

        <div className="sticky top-28 z-[9]" data-markdown-ignore>
          <div className={`grid ${GRID_CLASS}`}>
            <div className="bg-signoz_ink-500" />
            <div className="relative flex flex-col items-start gap-2.5 bg-[#14161a] px-3 py-4">
              <span className="text-base font-medium leading-7 text-[#eceef2]">SigNoz Cloud</span>
              <TrackingLink
                href="/teams/"
                clickType="Primary CTA"
                clickName="Sign Up Button"
                clickLocation="ClickStack Alternative Quick Evaluation"
                clickText="Get Started"
                className={cn(
                  buttonVariants({ variant: 'default', size: 'sm' }),
                  'w-40 tracking-wider'
                )}
              >
                Get Started
              </TrackingLink>
            </div>
            <div className="flex flex-col items-start gap-2.5 bg-signoz_ink-500 px-3 py-4">
              <span className="text-base font-medium leading-7 text-[#eceef2]">ClickStack</span>
            </div>
          </div>
          <div className="h-px w-full bg-[#23262e]" />
        </div>

        {/* Shared grid body */}
        <FeatureComparisonGridWithOcclusion
          columns={COLUMNS}
          sections={sections}
          gridClassName={GRID_CLASS}
          sectionHeadingSize="sm"
          stickyOffset="top-[215px]"
          stickyBg=""
          stickyZIndex="z-[8]"
          separator="border"
          featureCellClassName="pl-6 py-3"
          featureSectionClassName="pl-6 bg-signoz_ink-500"
          markdownColumnLabels={['Feature', 'SigNoz', 'ClickStack']}
        />
      </div>
    </div>
  )
}
